import { useState } from 'react';
import { DISCLAIMER_TEXT } from '../terms';
import SignOutLink from './SignOutLink';

// Reusable full-screen disclaimer gate. Used in two places:
//   1. CreateProfile — first-time acceptance during onboarding.
//   2. TermsGate — re-acceptance when the terms version changes.
// The "I Agree" button stays disabled until the user scrolls to the bottom of
// the text (basic enforceability) and while a save is in flight.
export default function DisclaimerScreen({
  title = 'Terms & Disclaimer',
  subtitle = 'Please read and accept the following before creating your profile.',
  agreeLabel = 'I Agree',
  busy = false,
  onAgree,
  showSignOut = false,
}) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 10) setScrolledToBottom(true);
  };

  const ready = scrolledToBottom && !busy;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>{title}</h2>
        <p style={styles.subtitle}>{subtitle}</p>
        <div style={styles.disclaimerBox} onScroll={handleScroll}>
          {DISCLAIMER_TEXT.split('\n').map((line, i) =>
            line.trim() === '' ? <br key={i} /> : <p key={i} style={styles.disclaimerPara}>{line}</p>
          )}
        </div>
        {!scrolledToBottom && (
          <p style={styles.scrollHint}>↓ Scroll to the bottom to continue</p>
        )}
        <button
          style={{ ...styles.button, opacity: ready ? 1 : 0.4, cursor: ready ? 'pointer' : 'not-allowed' }}
          onClick={() => ready && onAgree()}
          disabled={!ready}
        >
          {busy ? 'Saving…' : agreeLabel}
        </button>
        {showSignOut && <SignOutLink />}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100%', backgroundColor: 'var(--bg-page)', fontFamily: 'inherit', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 16px' },
  card: { backgroundColor: 'var(--bg-card)', borderRadius: '16px', boxShadow: 'var(--shadow-detail-card)', padding: '40px 36px', width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', gap: '12px', height: 'fit-content' },
  title: { margin: 0, fontSize: '24px', color: 'var(--text-primary)' },
  subtitle: { margin: '0 0 8px', color: 'var(--text-muted)', fontSize: '14px' },
  disclaimerBox: { height: '320px', overflowY: 'scroll', border: '1px solid var(--border-hairline)', borderRadius: '10px', padding: '16px', backgroundColor: 'var(--bg-card-alt)', fontSize: '13px', lineHeight: '1.6', color: 'var(--text-body)' },
  disclaimerPara: { margin: '0 0 4px' },
  scrollHint: { margin: '4px 0 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' },
  button: { padding: '14px', borderRadius: '10px', border: 'none', backgroundColor: 'var(--btn-solid-bg)', color: 'white', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
};
